/**
 * Account sayfası cache güncelleme yardımcıları
 * Talep/sipariş ekleme/silme işlemlerinde cache'i otomatik günceller
 */

export type UpdateType = 'request' | 'order';
export type OperationType = 'add' | 'delete';

const PENDING_UPDATES_KEY = 'account-pending-updates';

export interface PendingUpdates {
  request: number;
  order: number;
}

/**
 * Ortak cache güncelleme fonksiyonu
 */
const updateAccountCache = (type: UpdateType, operation: OperationType) => {
  try {
    // 1. Event tetikle (Account sayfası açıksa anında güncellenir)
    const eventNames = {
      request: { add: 'newServiceRequestCreated', delete: 'serviceRequestDeleted' },
      order: { add: 'newOrderCreated', delete: 'orderDeleted' }
    };
    
    const eventName = eventNames[type][operation];
    window.dispatchEvent(new CustomEvent(eventName));
    
    // 2. Pending update olarak kaydet (Account sayfası kapalıysa)
    const existing = localStorage.getItem(PENDING_UPDATES_KEY);
    const updates: PendingUpdates = existing ? JSON.parse(existing) : { request: 0, order: 0 };
    const increment = operation === 'add' ? 1 : -1;
    updates[type] = (updates[type] || 0) + increment;
    localStorage.setItem(PENDING_UPDATES_KEY, JSON.stringify(updates));
    
  } catch (error) {
    console.error(`Account cache ${operation} hatası:`, error);
  }
};

/**
 * Yeni talep/sipariş oluşturulduğunda çağrılır
 */
export const notifyAccountCacheUpdate = (type: UpdateType) => {
  updateAccountCache(type, 'add');
};

/**
 * Talep/sipariş silindiğinde çağrılır
 */
export const notifyAccountCacheDelete = (type: UpdateType) => {
  updateAccountCache(type, 'delete');
};

/**
 * Account sayfası yüklendiğinde bekleyen güncellemeleri uygular
 */
export const applyPendingAccountUpdates = (
  currentStats: any,
  setStats: (updater: (prev: any) => any) => void,
  setCachedData: (data: any) => void
) => {
  try {
    const pending = localStorage.getItem(PENDING_UPDATES_KEY);
    if (pending) {
      const updates: PendingUpdates = JSON.parse(pending);
      
      if (updates.request !== 0 || updates.order !== 0) {
        setStats((prevStats: any) => {
          const newStats = {
            ...prevStats,
            pendingTotal: Math.max(0, prevStats.pendingTotal + (updates.request || 0)),
            ordersPendingTotal: Math.max(0, prevStats.ordersPendingTotal + (updates.order || 0))
          };
          
          // Cache'i güncelle
          setCachedData(newStats);
          
          return newStats;
        });
      }
      
      // Pending updates'i temizle
      localStorage.removeItem(PENDING_UPDATES_KEY);
    }
  } catch (error) {
    console.error('Pending updates uygulama hatası:', error);
  }
};

/**
 * Account sayfasında event listener'lar için ortak güncelleme fonksiyonu
 */
const updateAccountCacheCount = (
  type: UpdateType,
  operation: OperationType,
  loading: boolean,
  setStats: (updater: (prev: any) => any) => void,
  setCachedData: (data: any) => void
) => {
  // Eğer sayfa loading durumundaysa, pending update olarak kaydet
  if (loading) {
    const existing = localStorage.getItem(PENDING_UPDATES_KEY);
    const updates: PendingUpdates = existing ? JSON.parse(existing) : { request: 0, order: 0 };
    updates[type] = (updates[type] || 0) + (operation === 'add' ? 1 : -1);
    localStorage.setItem(PENDING_UPDATES_KEY, JSON.stringify(updates));
    return;
  }
  
  // Sayfa hazırsa direkt state'i güncelle
  setStats((prevStats: any) => {
    const increment = operation === 'add' ? 1 : -1;
    const newStats = {
      ...prevStats,
      ...(type === 'request' 
        ? { pendingTotal: Math.max(0, prevStats.pendingTotal + increment) }
        : { ordersPendingTotal: Math.max(0, prevStats.ordersPendingTotal + increment) }
      )
    };
    
    // Cache'i de güncelle
    setCachedData(newStats);
    
    return newStats;
  });
};

/**
 * Account sayfasında event listener'lar için kullanılır (ekleme)
 */
export const incrementAccountCacheCount = (
  type: UpdateType,
  loading: boolean,
  setStats: (updater: (prev: any) => any) => void,
  setCachedData: (data: any) => void
) => {
  updateAccountCacheCount(type, 'add', loading, setStats, setCachedData);
};

/**
 * Account sayfasında event listener'lar için kullanılır (silme)
 */
export const decrementAccountCacheCount = (
  type: UpdateType,
  loading: boolean,
  setStats: (updater: (prev: any) => any) => void,
  setCachedData: (data: any) => void
) => {
  updateAccountCacheCount(type, 'delete', loading, setStats, setCachedData);
};
